import {useQuery} from "@tanstack/react-query";
import {Copy, Loader2, Pencil} from "lucide-react";
import {useState} from "react";
import type {FC} from "react";
import {Link, useParams} from "react-router";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface AdminUserDetailResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    role: "USER" | "ADMIN";
    createdAt: string | null;
    updatedAt: string | null;
  };
  stats: {
    orgCount: number;
    orgAdminCount: number;
    recordsCreated: number;
    recordsUpdated: number;
    loginCount: number;
    activeSessionCount: number;
    firstActivityAt: string | null;
    lastActivityAt: string | null;
    lastLoginAt: string | null;
  };
  memberships: Array<{
    orgUserId: string;
    orgId: string;
    orgName: string;
    orgSlug: string;
    role: "USER" | "ADMIN";
    createdAt: string;
    createdBy: string | null;
    updatedAt: string;
  }>;
  recentRecords: Array<{
    id: string;
    orgId: string | null;
    orgName: string | null;
    englishName: string | null;
    scientificName: string | null;
    dtm: string;
    count: number | null;
    lat: number | null;
    lng: number | null;
    locationNote: string | null;
    note: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  recentActivity: Array<{
    id: string;
    type: string;
    createdAt: string;
  }>;
  sessions: Array<{
    id: string;
    createdAt: string;
    expiresAt: string;
  }>;
  authMethods: Array<{
    provider: string;
    providerUserId: string | null;
    hasPassword: boolean;
    hasResetToken: boolean;
    resetTokenExpiresAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
}

const formatDateTime = (value: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const statCards = [
  ["orgCount", "Organisations"],
  ["orgAdminCount", "Org Admin Roles"],
  ["recordsCreated", "Records Created"],
  ["recordsUpdated", "Records Updated"],
  ["loginCount", "Logins"],
  ["activeSessionCount", "Active Sessions"],
] as const;

export const AdminUserDetail: FC = () => {
  const {userId} = useParams<{userId: string}>();
  const [copied, setCopied] = useState<string | null>(null);

  const {data, isLoading, error} = useQuery<AdminUserDetailResponse>({
    queryKey: ["user-detail", userId],
    queryFn: async () => {
      const res = await fetch(`/api/user/${userId}/detail`);
      if (!res.ok) throw new Error("Failed to fetch user detail");
      return res.json();
    },
    enabled: !!userId,
  });

  const copyValue = async (label: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1500);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-red-600">
        Failed to load user details.
      </div>
    );
  }

  const {user, stats} = data;

  return (
    <div className="container mx-auto space-y-6 p-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{user.name || "Unnamed user"}</h1>
            <Badge variant={user.role === "ADMIN" ? "default" : "secondary"}>
              {user.role === "ADMIN" ? "System Admin" : "User"}
            </Badge>
          </div>
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span>{user.email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyValue("email", user.email)}
            >
              <Copy className="mr-1 h-4 w-4" />
              {copied === "email" ? "Copied" : "Email"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyValue("id", user.id)}
            >
              <Copy className="mr-1 h-4 w-4" />
              {copied === "id" ? "Copied" : "ID"}
            </Button>
          </div>
        </div>
        <Button asChild>
          <Link to={`/app/admin/user/${user.id}/edit`}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit User
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        {statCards.map(([key, label]) => (
          <Card key={key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats[key]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <DetailRow label="Created" value={formatDateTime(user.createdAt)} />
            <DetailRow label="Updated" value={formatDateTime(user.updatedAt)} />
            <DetailRow label="First activity" value={formatDateTime(stats.firstActivityAt)} />
            <DetailRow label="Last activity" value={formatDateTime(stats.lastActivityAt)} />
            <DetailRow label="Last login" value={formatDateTime(stats.lastLoginAt)} />
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Authentication</CardTitle>
          </CardHeader>
          <CardContent>
            {data.authMethods.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Password</TableHead>
                    <TableHead>Reset token</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.authMethods.map((auth) => (
                    <TableRow key={`${auth.provider}-${auth.providerUserId ?? "email"}`}>
                      <TableCell>{auth.provider}</TableCell>
                      <TableCell>{auth.hasPassword ? "Configured" : "-"}</TableCell>
                      <TableCell>
                        {auth.hasResetToken
                          ? `Expires ${formatDateTime(auth.resetTokenExpiresAt)}`
                          : "-"}
                      </TableCell>
                      <TableCell>{formatDateTime(auth.updatedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-muted-foreground text-sm">No auth methods found.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organisation Memberships</CardTitle>
        </CardHeader>
        <CardContent>
          {data.memberships.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Added By</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.memberships.map((membership) => (
                  <TableRow key={membership.orgUserId}>
                    <TableCell>
                      <div className="font-medium">{membership.orgName}</div>
                      <div className="text-muted-foreground text-xs">{membership.orgSlug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={membership.role === "ADMIN" ? "default" : "secondary"}>
                        {membership.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDateTime(membership.createdAt)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {membership.createdBy ?? "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/app/admin/orgs/${membership.orgId}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-muted-foreground text-sm">No memberships found.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Records Created</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentRecords.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Species</TableHead>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Observed</TableHead>
                  <TableHead>Count</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <div className="font-medium">{record.englishName ?? "Unknown"}</div>
                      <div className="text-muted-foreground text-xs">
                        {record.scientificName ?? "-"}
                      </div>
                    </TableCell>
                    <TableCell>{record.orgName ?? "-"}</TableCell>
                    <TableCell>{formatDateTime(record.dtm)}</TableCell>
                    <TableCell>{record.count ?? "-"}</TableCell>
                    <TableCell>
                      {record.lat !== null && record.lng !== null
                        ? `${record.lat.toFixed(5)}, ${record.lng.toFixed(5)}`
                        : record.locationNote ?? "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/app/admin/record/${record.id}`}>View</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-muted-foreground text-sm">
              This user has not created records yet.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Activity Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {data.recentActivity.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recentActivity.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>{activity.type.replace(/_/g, " ")}</TableCell>
                      <TableCell>{formatDateTime(activity.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-muted-foreground text-sm">No activity logged.</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {data.sessions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Session ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>{formatDateTime(session.createdAt)}</TableCell>
                      <TableCell>{formatDateTime(session.expiresAt)}</TableCell>
                      <TableCell className="font-mono text-xs">{session.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-muted-foreground text-sm">No sessions found.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const DetailRow = ({label, value}: {label: string; value: string}) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);
